import React, {useEffect, useState} from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	InputAdornment,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import {AccountingForecastSettings} from "arteco-api-client-ts";
import {btnSx} from "../../../../components/kit";

const parse = (s: string) => s.trim() === "" ? undefined : Number(s.replace(",", "."));

/** Parámetros de la previsión (#14560): colchón objetivo, tipo del Impuesto de Sociedades y si se provisiona el rápel. */
export const ForecastSettingsDialog = ({open, value, onClose, onSave}: {
	open: boolean, value: AccountingForecastSettings, onClose: () => void, onSave: (s: AccountingForecastSettings) => Promise<unknown>,
}) => {
	const [cushion, setCushion] = useState("");
	const [taxPct, setTaxPct] = useState("");
	const [provision, setProvision] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			setCushion(String(value.cushionMonths ?? ""));
			setTaxPct(value.corporateTaxPct === undefined || value.corporateTaxPct === null ? "" : String(value.corporateTaxPct));
			setProvision(value.provisionRebate ?? true);
		}
	}, [open, value]);

	const cushionValue = parse(cushion);
	const taxValue = parse(taxPct);
	const cushionInvalid = cushionValue === undefined || !(cushionValue > 0 && cushionValue <= 60);
	const taxInvalid = taxValue !== undefined && !(taxValue >= 0 && taxValue <= 100);

	const save = () => {
		setSaving(true);
		onSave({cushionMonths: cushionValue!, corporateTaxPct: taxValue, provisionRebate: provision})
			.then(onClose)
			.finally(() => setSaving(false));
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle sx={{fontWeight: 700}}>Parámetros de la previsión</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
					Lo demás sale de los datos: la facturación prevista, el diario, las nóminas y el calendario fiscal.
				</Typography>
				<Box sx={{display: "grid", gap: 2}}>
					<TextField label="Colchón objetivo" size="small" value={cushion} onChange={e => setCushion(e.target.value)}
							   error={cushionInvalid} inputProps={{inputMode: "decimal"}}
							   helperText="Meses de costes fijos (nóminas y gastos) que deben quedar en el banco. Por debajo, en rojo."
							   InputProps={{endAdornment: <InputAdornment position="end">meses</InputAdornment>}}/>
					<TextField label="Tipo del Impuesto de Sociedades" size="small" value={taxPct} onChange={e => setTaxPct(e.target.value)}
							   error={taxInvalid} inputProps={{inputMode: "decimal"}}
							   helperText="Vacío: no se estima el impuesto ni el resultado después de impuestos."
							   InputProps={{endAdornment: <InputAdornment position="end">%</InputAdornment>}}/>
					<FormControlLabel control={<Switch checked={provision} onChange={e => setProvision(e.target.checked)}/>}
									  label={<Box>
										  <Typography variant="body2" fontWeight={600}>Provisionar el rápel del año al cierre</Typography>
										  <Typography variant="caption" color="text.secondary">
											  Sin provisión, el rápel del año entra en la contabilidad del siguiente, con su nota de abono.
										  </Typography>
									  </Box>}/>
				</Box>
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 2}}>
				<Button onClick={onClose} sx={btnSx}>Cancelar</Button>
				<Button variant="contained" onClick={save} disabled={saving || cushionInvalid || taxInvalid} sx={btnSx}>Guardar parámetros</Button>
			</DialogActions>
		</Dialog>
	);
};
